import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Minus, Edit, GitCompare, ArrowLeftRight, ChevronRight } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

export const VisualDiffViewer = ({ 
  isOpen, 
  onClose, 
  workflowId, 
  versionAId, 
  versionBId 
}) => {
  const [diffData, setDiffData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('unified'); // 'unified' or 'side-by-side'

  useEffect(() => {
    if (isOpen && workflowId && versionAId && versionBId) {
      fetchDiff();
    }
  }, [isOpen, workflowId, versionAId, versionBId]);

  const fetchDiff = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/workflows/${workflowId}/diff/${versionAId}/${versionBId}`,
        { withCredentials: true }
      );
      setDiffData(response.data);
    } catch (error) {
      console.error("Failed to fetch diff:", error);
      toast.error("Failed to load version diff");
    } finally {
      setLoading(false);
    }
  };

  const renderNodeBadge = (node, changeType) => {
    const colors = {
      added: 'bg-green-100 text-green-700 border-green-300',
      removed: 'bg-red-100 text-red-700 border-red-300',
      modified: 'bg-orange-100 text-orange-700 border-orange-300'
    };

    const icons = {
      added: <Plus className="w-3 h-3" />,
      removed: <Minus className="w-3 h-3" />,
      modified: <Edit className="w-3 h-3" />
    };

    return (
      <div className={`border rounded-lg p-3 ${colors[changeType]}`}>
        <div className="flex items-center gap-2 mb-2">
          {icons[changeType]}
          <span className="font-medium text-sm">{node.type}</span>
          <Badge variant="outline" className="text-xs">
            {changeType}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">ID: {node.id}</p>
        {changeType === 'modified' && node.changes && (
          <div className="mt-2 space-y-1">
            {node.changes.slice(0, 3).map((change, idx) => (
              <p key={idx} className="text-xs">
                • {change.field}: {change.change_type}
              </p>
            ))}
            {node.changes.length > 3 && (
              <p className="text-xs text-muted-foreground">
                +{node.changes.length - 3} more changes
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderEdgeBadge = (edge, changeType) => {
    const colors = {
      added: 'bg-green-50 text-green-700 border-green-200',
      removed: 'bg-red-50 text-red-700 border-red-200'
    };

    const icons = {
      added: <Plus className="w-3 h-3" />,
      removed: <Minus className="w-3 h-3" />
    };

    return (
      <div className={`border rounded p-2 ${colors[changeType]} flex items-center gap-2`}>
        {icons[changeType]}
        <span className="text-xs">
          {edge.source} <ChevronRight className="w-3 h-3 inline" /> {edge.target}
        </span>
      </div>
    );
  };

  if (!diffData && !loading) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="w-5 h-5 text-primary" />
            Comparing Versions
          </DialogTitle>
          <DialogDescription>
            {diffData && (
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">v{diffData.version_a_number}</Badge>
                  <span className="text-xs text-muted-foreground">
                    {diffData.version_a_metadata?.commit_message}
                  </span>
                </div>
                <ArrowLeftRight className="w-4 h-4" />
                <div className="flex items-center gap-2">
                  <Badge variant="outline">v{diffData.version_b_number}</Badge>
                  <span className="text-xs text-muted-foreground">
                    {diffData.version_b_metadata?.commit_message}
                  </span>
                </div>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin text-4xl mb-4">⏳</div>
              <p className="text-muted-foreground">Computing diff...</p>
            </div>
          </div>
        ) : diffData ? (
          <div className="space-y-4">
            {/* Summary */}
            <div className="border rounded-lg p-4 bg-muted/30">
              <h4 className="font-medium mb-3">Summary</h4>
              <div className="flex flex-wrap gap-2">
                {diffData.summary.nodes_added > 0 && (
                  <Badge className="bg-green-100 text-green-700">
                    <Plus className="w-3 h-3 mr-1" />
                    {diffData.summary.nodes_added} node(s) added
                  </Badge>
                )}
                {diffData.summary.nodes_removed > 0 && (
                  <Badge className="bg-red-100 text-red-700">
                    <Minus className="w-3 h-3 mr-1" />
                    {diffData.summary.nodes_removed} node(s) removed
                  </Badge>
                )}
                {diffData.summary.nodes_modified > 0 && (
                  <Badge className="bg-orange-100 text-orange-700">
                    <Edit className="w-3 h-3 mr-1" />
                    {diffData.summary.nodes_modified} node(s) modified
                  </Badge>
                )}
                {diffData.summary.edges_added > 0 && (
                  <Badge className="bg-green-100 text-green-700">
                    <Plus className="w-3 h-3 mr-1" />
                    {diffData.summary.edges_added} connection(s) added
                  </Badge>
                )}
                {diffData.summary.edges_removed > 0 && (
                  <Badge className="bg-red-100 text-red-700">
                    <Minus className="w-3 h-3 mr-1" />
                    {diffData.summary.edges_removed} connection(s) removed
                  </Badge>
                )}
                {diffData.summary.total_changes === 0 && (
                  <Badge variant="outline">No changes</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {diffData.summary_text}
              </p>
            </div>

            {/* Diff Content */}
            <Tabs defaultValue="nodes" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="nodes">Nodes</TabsTrigger>
                <TabsTrigger value="connections">Connections</TabsTrigger>
              </TabsList>

              <TabsContent value="nodes" className="space-y-4">
                <ScrollArea className="h-[400px] pr-4">
                  {/* Added Nodes */}
                  {diffData.nodes_added.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-medium text-green-700 mb-3 flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        Added Nodes ({diffData.nodes_added.length})
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        {diffData.nodes_added.map((node) => (
                          <div key={node.id}>
                            {renderNodeBadge(node, 'added')}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Removed Nodes */}
                  {diffData.nodes_removed.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-medium text-red-700 mb-3 flex items-center gap-2">
                        <Minus className="w-4 h-4" />
                        Removed Nodes ({diffData.nodes_removed.length})
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        {diffData.nodes_removed.map((node) => (
                          <div key={node.id}>
                            {renderNodeBadge(node, 'removed')}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Modified Nodes */}
                  {diffData.nodes_modified.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-medium text-orange-700 mb-3 flex items-center gap-2">
                        <Edit className="w-4 h-4" />
                        Modified Nodes ({diffData.nodes_modified.length})
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        {diffData.nodes_modified.map((node) => (
                          <div key={node.id}>
                            {renderNodeBadge(node, 'modified')}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {diffData.nodes_added.length === 0 && 
                   diffData.nodes_removed.length === 0 && 
                   diffData.nodes_modified.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No node changes
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="connections" className="space-y-4">
                <ScrollArea className="h-[400px] pr-4">
                  {/* Added Edges */}
                  {diffData.edges_added.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-medium text-green-700 mb-3 flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        Added Connections ({diffData.edges_added.length})
                      </h4>
                      <div className="space-y-2">
                        {diffData.edges_added.map((edge, idx) => (
                          <div key={idx}>
                            {renderEdgeBadge(edge, 'added')}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Removed Edges */}
                  {diffData.edges_removed.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-medium text-red-700 mb-3 flex items-center gap-2">
                        <Minus className="w-4 h-4" />
                        Removed Connections ({diffData.edges_removed.length})
                      </h4>
                      <div className="space-y-2">
                        {diffData.edges_removed.map((edge, idx) => (
                          <div key={idx}>
                            {renderEdgeBadge(edge, 'removed')}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {diffData.edges_added.length === 0 && 
                   diffData.edges_removed.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No connection changes
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
